import { differenceInYears, formatISO, parse } from 'date-fns'
import { List, Map } from 'immutable'
import { isArray, isEmpty } from 'lodash-es'
import { createSelector } from 'reselect'

import { PREFERRED_APPOINTMENT_MODALITIES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { COUNTRY_CATEGORY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { DEFAULT_PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { ImmutableTypedMap } from '@lyrahealth-inc/shared-app-logic/src/features/common/types/Immutable'
import { getHelpEmailAddress } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import {
  computeRemainderSessions,
  computeRemainderUsed,
  isUnlimitedSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { isAgeTeen } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { DEFAULT_LANGUAGE } from '@lyrahealth-inc/shared-app-logic/src/features/intl/constants'
import {
  getManagerCoachingSeatEligibility as getManagerCoachingSeatEligibilityShared,
  ManagerCoachingSeatEligibility,
  SeatBasedLicensesResponse,
} from '@lyrahealth-inc/shared-app-logic/src/features/managerHub/managerCoachingSeatEligibility'
import { getAge } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { ProgramCategory } from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'
import {
  NotificationItemState,
  NotificationItemStatus,
  User,
} from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { INTERNATIONAL_CONSENT_USER_STATUS } from '../../common/constants/appConstants'
import { getUsedSessionCount, getUsedSessionCountByProgram } from '../../features/healthPlan/data/healthPlanSelectors'
import {
  getOnboardLocation,
  getOnboardTimezoneForAvailabilityRequest,
} from '../../features/onboard/data/onboardSelectors'
import { getIsDetectedCountryInternational } from '../appGlobals/appGlobalsSelectors'
import {
  getCancellationLimit,
  getCustomerName,
  getCustomerProductType,
  getCustomerSupportedPrograms,
  getIsCustomerInternational,
  getIsCustomerLyra,
  getNumberOfSessions,
  getNumberOfSessionsByProgram,
} from '../customer/customerSelectors'
import {
  getAdultTriageDisabled,
  getForceAdultFlowsToHPI,
  getIsClientDisabled,
  getIsEapHpiInProductNotificationsEnabled,
  getIsFavoritedProvidersEnabled,
} from '../featureFlags/featureFlagSelectors'

export const getUser = (state: Map<string, any>) => state?.get('user')

export const getId = createSelector(getUser, (user) => user?.get('lyraId'))

export const getHealthPlan = createSelector(getUser, (user) => user?.get('healthPlan'))

export const getUsername = createSelector(getUser, (user) => user?.get('username'))

export const getUserLegalFirstName = createSelector(getUser, (user) => user?.get('firstname'))

export const getUserLegalLastName = createSelector(getUser, (user) => user?.get('lastname'))

export const getUserDisplayFirstName = createSelector(
  getUser,
  (user) => user?.get('preferredFirstName') || user?.get('firstname'),
)

export const getUserDisplayLastName = createSelector(
  getUser,
  (user) => user?.get('preferredLastName') || user?.get('lastname'),
)

export const getUserPreferredFirstName = createSelector(getUser, (user) => user?.get('preferredFirstName'))

export const getUserPreferredLastName = createSelector(getUser, (user) => user?.get('preferredLastName'))

/**
 * WARNING: Backend returns DOB in `MM/dd/yyyy` format, which does not conform to JavaScript's date time string format,
 * a variant of ISO 8601. `Date()` is not gauranteed to work with other formats, and behavior may vary across browsers.
 *
 * See reference: [Date#date_time_string_format](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format)
 *
 * If you need a `Date` object, use `getUserDOBDate` selector
 */
export const getUserDOB = createSelector(getUser, (user) => user?.get('dob'))

export const getUserDOBDate = createSelector(getUserDOB, (userDOB: string | undefined) =>
  userDOB ? parse(userDOB, 'MM/dd/yyyy', new Date()) : undefined,
)

/**
 * Returns user DOB as a ISO 8601 date string, ex: 2023-01-02
 */
export const getUserDOBISO = createSelector(getUserDOBDate, (userDOBDate: Date | undefined) =>
  userDOBDate ? formatISO(userDOBDate, { representation: 'date' }) : undefined,
)

export const getUserGender = createSelector(getUser, (user) => user?.get('gender'))

export const getUserGenderString = createSelector(getUserGender, (gender) => gender?.get('gender'))

export const getUserAge = createSelector(getUserDOBDate, (userDOBDate: Date | undefined) => {
  return userDOBDate ? differenceInYears(new Date(), userDOBDate) : undefined
})

export const getIsUserMinor = createSelector(getUserAge, (userAge) => {
  return userAge ? userAge <= 17 : undefined
})

export const getIsUserTeen = createSelector(getUserAge, (age) => {
  return age ? isAgeTeen(age) : false
})

export const getIsUserMfaEnabled = createSelector(getUser, (user) => user?.get('mfa'))

export const getUserFriendlyLyraCode = createSelector(
  getUser,
  (user) => user?.get('friendlyLyraCode') as string | undefined,
)

// This selector is only used for CNT booking since the timeZoneId may be different than the logged in user (aka Care Navigator booking for a client)
export const getClientLocationTimeZone = createSelector(
  (state: $TSFixMe) => getOnboardLocation(state),
  (onboard) => onboard?.get('timeZoneId'),
)

// Get the user's time zone if it is set explicitly. Otherwise,
// default to the time zone of the search location they entered
// when searching for provider availability. This is used to display
// dates and times localized according to the user's time zone.
// When localizing dates and times, we want to follow this hierarchy
// for obtaining the right time zone:
// 1) user's explicitly set time zone (`user.timeZone`)
// 2) time zone corresponding to the user's search location
// 3) the browser guess of the user's time zone (this comes from the default of the `getOnboardTimezoneForAvailabilityRequest` onboard selector)
export const getTimeZoneOrDefault = createSelector(
  getUser,
  (state: $TSFixMe) => getOnboardTimezoneForAvailabilityRequest(state),
  (user: $TSFixMe, timeZoneForAvailabilityRequest: $TSFixMe) => user?.get('timeZone') ?? timeZoneForAvailabilityRequest,
)

// Get the user's saved time zone WITHOUT a default. This is needed for the user's profile so that
// we can show when the user does not have a saved time zone. We don't want to display any kind of
// default on the profile so the user will be aware that the time zone is missing from their
// profile information.
export const getTimeZone = createSelector(getUser, (user) => user?.get('timeZone'))

export const getTimeZoneOrDefaultIgnoreOnboarding = createSelector(getUser, (user) => {
  const userTimeZone = user?.get('timeZone')
  if (userTimeZone) {
    if (userTimeZone === 'GMT') {
      return 'Etc/GMT'
    }
    return userTimeZone
  }
  return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone
})

export const getIsUserInternational = createSelector(getUser, (user) => user?.get('isInternational') ?? false)

export const getIsUserInternationalAndMinor = createSelector(
  getIsUserMinor,
  getIsUserInternational,
  (isUserMinor, isUserInternational) => isUserMinor && isUserInternational,
)

// gets the users legal first and legal last name locally formatted
export const getUserLegalName = createSelector(
  getUserLegalFirstName,
  getUserLegalLastName,
  getIsUserInternational,
  (firstName: string, lastName: string, isInternational: boolean) => {
    return isInternational ? `${lastName}, ${firstName}` : `${firstName} ${lastName}`
  },
)

// gets the users display first and display last name locally formatted
export const getUserDisplayName = createSelector(
  [
    getUserDisplayFirstName,
    getUserDisplayLastName,
    getUserLegalFirstName,
    getUserLegalLastName,
    getIsUserInternational,
    (_, isPreferredNameEnabled) => isPreferredNameEnabled,
  ],
  (displayFirstName, displayLastName, legalFirstName, legalLastName, isUserInternational, isPreferredNameEnabled) => {
    const firstName = isPreferredNameEnabled ? displayFirstName || legalFirstName : legalFirstName
    const lastName = isPreferredNameEnabled ? displayLastName || legalLastName : legalLastName
    return isUserInternational ? `${lastName}, ${firstName}` : `${firstName} ${lastName}`
  },
)
export const getUserCountryCategory = createSelector(getIsUserInternational, (isUserInternational) => {
  return isUserInternational ? COUNTRY_CATEGORY.INTERNATIONAL : COUNTRY_CATEGORY.DOMESTIC
})

export const getUserInternationalConsent = createSelector(
  getUser,
  (user) => user?.get('internationalConsent') ?? INTERNATIONAL_CONSENT_USER_STATUS.NONE,
)

export const getIsUserLoggedIn = createSelector(getUser, (user) => user.size > 0)

export const getUserHasPastFavoritedProviders = createSelector(
  getUser,
  (user) => user?.get('hasPastFavoritedProviders') ?? false,
)

export const getUserPreferredAppointmentTypes = createSelector(getUser, (user) =>
  user?.get('preferredAppointmentTypes', List()),
)

// Do not use to populate member preferences in search payload -- use member preferences shared-app-logic utils
export const getUserModalityPreference = createSelector(getUserPreferredAppointmentTypes, (modalities) => {
  return modalities.size > 1 ? PREFERRED_APPOINTMENT_MODALITIES.NO_PREFERENCE : modalities.join(',')
})

export const getHasUserProvidedInternationalConsentResponse = createSelector(
  getUserInternationalConsent,
  (userInternationalConsent) => {
    return userInternationalConsent !== INTERNATIONAL_CONSENT_USER_STATUS.NONE
  },
)

export const getIsInternationalUserWithDeclinedInternationalConsent = createSelector(
  getIsUserInternational,
  getUserInternationalConsent,
  (isUserInternational, userInternationalConsent) => {
    return isUserInternational && userInternationalConsent === INTERNATIONAL_CONSENT_USER_STATUS.DECLINED
  },
)

export const getIsUserInternationalAndOfInternationalCustomer = createSelector(
  getIsUserLoggedIn,
  (state: $TSFixMe) => getIsCustomerInternational(state),
  getIsUserInternational,
  (isLoggedIn: $TSFixMe, isCustomerInternational: $TSFixMe, isUserInternational: $TSFixMe) => {
    return isLoggedIn && isCustomerInternational && isUserInternational
  },
)

export const getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer = createSelector(
  getIsUserLoggedIn,
  (state: $TSFixMe) => getIsCustomerInternational(state),
  (state: $TSFixMe) => getIsDetectedCountryInternational(state),
  (isUserLoggedIn: $TSFixMe, isCustomerInternational: $TSFixMe, isDetectedCountryInternational: $TSFixMe) => {
    return isCustomerInternational && !isUserLoggedIn && isDetectedCountryInternational
  },
)

export const getCareTeamEmail = createSelector(
  (state: $TSFixMe) => getCustomerName(state),
  (state: $TSFixMe) => getIsUserInternational(state),
  (customerName, isUserInternational) => {
    return getHelpEmailAddress({
      customerName: customerName ?? '',
      isInternationalUser: isUserInternational,
    })
  },
)

export const getShouldHideChatForUser = createSelector(
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserInternationalAndOfInternationalCustomer,
  (state: $TSFixMe) => getIsCustomerLyra(state),
  (
    isLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
    isUserInternationalAndOfInternationalCustomer,
    isCustomerLyra,
  ): boolean => {
    return (
      isCustomerLyra ||
      isLoggedOutUserAssumedInternationalAndOfInternationalCustomer ||
      isUserInternationalAndOfInternationalCustomer
    )
  },
)

export const getUserCustomerCountries = createSelector(getUser, (user) => user?.get('customerCountries') ?? Map())

export const getUserCountryName = createSelector(
  getUserCustomerCountries,
  (customerCountries) => customerCountries?.get('countryName') ?? '',
)

export const getUserCountryCode = createSelector(
  getUserCustomerCountries,
  (customerCountries) => customerCountries?.get('countryIsoCode') ?? '',
)

export const getUserCustomerCountryId = createSelector(getUserCustomerCountries, (customerCountries) =>
  customerCountries?.get('id'),
)

export const getUserCountryId = createSelector(getUserCustomerCountries, (customerCountries) =>
  customerCountries?.get('countryId'),
)

export const getUserCountryOutOfCountryNumbersList = createSelector(getUserCustomerCountries, (customerCountries) =>
  customerCountries?.get('countryOutOfCountryNumbersList'),
)

export const getUserCountryTollFreeNumbersList = createSelector(getUserCustomerCountries, (customerCountries) =>
  customerCountries?.get('countryTollFreeNumbersList'),
)

export const getAccessToInternationallySupportedTreatmentOptions = createSelector(
  getIsUserInternational,
  (state: $TSFixMe) => getCustomerSupportedPrograms(state),
  (isUserInternational: $TSFixMe, customerSupportedPrograms: $TSFixMe) => {
    return isUserInternational && !isEmpty(customerSupportedPrograms.toJS())
  },
)

export const getShouldUserSeeInternationalConsentForDataCollection = createSelector(
  getAccessToInternationallySupportedTreatmentOptions,
  getHasUserProvidedInternationalConsentResponse,
  (hasAccessToInternationallySupportedTreatmentOptions, hasUserProvidedInternationalConsentResponse) => {
    return hasAccessToInternationallySupportedTreatmentOptions && !hasUserProvidedInternationalConsentResponse
  },
)

export const getUserPhoneNumber = createSelector(getUser, (user) => user?.get('phone'))

// NOTE: If you're looking to use this selector, consider using `getUserDisplayLanguageOrDefault` instead.
// Accounts created prior to 2023 don't have a display language set, which can cause strange behavior
// with code that's expecting a display language to always be set.
// Only use this selector if you need to be working with the exact value of the display language that the
// user has associated with their account and can't use default values.
export const getUserDisplayLanguage = createSelector(getUser, (user) => user?.get('displayLanguage'))

export const getUserDisplayLanguageOrDefault = createSelector(
  getUser,
  getIsUserLoggedIn,
  getUserDisplayLanguage,
  (user, isLoggedIn, displayLanguage) => (isLoggedIn ? (displayLanguage ?? DEFAULT_LANGUAGE) : undefined),
)

// If you use this selector, be sure to call the endpoint getDirectDedicatedCareNavigatorLink to keep this selector updated.
export const getUserDirectDedicatedCareNavigatorLink = createSelector(getUser, (user) =>
  user?.get('directDedicatedCareNavigatorLink'),
)

export const getUserSalesforceId = createSelector(getUser, (user) => user?.get('salesforceId'))

export const isUserCountryUK = createSelector(getUserCountryCode, (countryCode) => {
  return countryCode && countryCode === 'GB'
})

export const getUserChildren = createSelector(getUser, (user) =>
  user?.get('children')?.filter((child: ImmutableTypedMap<User>) => !child.get('disabled')),
)

// get user's children that are 18 or older
export const getAdultChildren = createSelector(getUserChildren, (children) => {
  return children
    ?.toJS()
    ?.filter((childUser: User) => getAge({ date: childUser?.dob ?? '', dateFormat: 'MM/DD/YYYY' }) >= 18)
})

// get user's children that have registered their own LW account (e.g. LCT teen)
export const getUserRegisteredChildren = createSelector(getUserChildren, (children) => {
  return children?.toJS()?.filter((childUser: User) => childUser.emailVerified === true)
})

// get user's children that are under 18 and not registered their own accounts
export const getUserUnregisteredMinorChildren = createSelector(
  [getUserChildren, getAdultChildren, getUserRegisteredChildren],
  (
    children: ImmutableTypedMap<User[] | undefined>,
    adultChildren: User[] | undefined,
    registeredChildren: User[] | undefined,
  ) =>
    children
      ?.toJS()
      ?.filter(
        (childUser: User) =>
          !adultChildren?.some((c) => c.lyraId === childUser.lyraId) &&
          !registeredChildren?.some((c) => c.lyraId === childUser.lyraId),
      ),
)

// Even if a user has no children the API will respond with [], which will be placed into redux state
export const getHasFetchedChildUsers = createSelector(getUserChildren, (children) => {
  return isArray(children?.toJS())
})

export const getUserRemainingTime = createSelector(getUser, (user) => user?.get('sessionRemainingTime'))

export const getUserEmployeeGroup = createSelector(getUser, (user) => user?.get('employeeGroup'))

export const getUserHasIndicatedIsGuardian = createSelector(getUser, (user) => user?.get('hasIndicatedIsGuardian'))

export const getUserDedicatedCareNavigatorInfo = createSelector(getUser, (user) =>
  user?.get('dedicatedCareNavigatorInfo'),
)

export const getUserNotifications = createSelector(getUser, (user) => {
  return user?.get('notifications')
})

export const getUpdateEmailAddressNotificationState = createSelector(
  getUserNotifications,
  (notificationStates): NotificationItemState => {
    return notificationStates?.get('updateEmailAddress')
  },
)

export const getCollectAutoEnrollmentBackgroundInfo = createSelector(
  getUserNotifications,
  (notificationStates): NotificationItemState => {
    return notificationStates?.get('collectAutoEnrollmentBackgroundInfo')
  },
)

export const getUpdatePhoneNumberNotificationState = createSelector(
  getUserNotifications,
  (notificationStates): NotificationItemState => {
    return notificationStates?.get('updatePhoneNumber')
  },
)

export const getEapHpiHomebaseBannerNotificationState = createSelector(
  getUserNotifications,
  (notificationStates): NotificationItemState => {
    return notificationStates?.get('eapHpiHomebaseBanner')
  },
)

export const getEapHpiAccountBannerNotificationState = createSelector(
  getUserNotifications,
  (notificationStates): NotificationItemState => {
    return notificationStates?.get('eapHpiAccountBanner')
  },
)

export const getShouldShowAccountBadge = createSelector(
  [getUserNotifications, getIsEapHpiInProductNotificationsEnabled],
  (notificationStates_, isEapHpiInProductNotificationsEnabled): boolean => {
    const notificationStates: User['notifications'] = notificationStates_?.toJS()
    if (!notificationStates) {
      return false
    }
    const notifications: (keyof typeof notificationStates)[] = ['updateEmailAddress', 'updatePhoneNumber']

    if (isEapHpiInProductNotificationsEnabled) {
      notifications.push('eapHpiAccountBanner')
    }

    return notifications.some((name) => {
      const notificationState = notificationStates[name]
      return (
        notificationState != null &&
        (notificationState === NotificationItemStatus.NEW || notificationState === NotificationItemStatus.SEEN)
      )
    })
  },
)

export const getSelfSearchAvailable = createSelector(
  [getAdultTriageDisabled, getIsUserMinor],
  (adultTriageDisabled, userIsMinor) => {
    return userIsMinor || !adultTriageDisabled
  },
)

/**
 * Returns the EAP session limit for the current user
 *
 * For Amazon, when flag forceAdultFlowsToHPI is enabled:
 * Force adult users to have a session limit of 0 for all programs, except Coaching For Parents.
 * Minors should return the full session limit.
 *
 * @param withoutOverrides (optional) When true, return the actual session limit without any overrides.
 */
export const getUserEAPSessionLimit = createSelector(
  [
    getForceAdultFlowsToHPI,
    getIsUserMinor,
    (state) => getNumberOfSessions(state),
    (_state, withoutOverrides: boolean = false) => withoutOverrides,
  ],
  (forceAdultFlowsToHPIForAmazon, userIsMinor, numberOfSessionsCustomerProperty, withoutOverrides) => {
    if (!withoutOverrides && forceAdultFlowsToHPIForAmazon && !userIsMinor) {
      return 0
    }

    return parseInt(numberOfSessionsCustomerProperty ?? '0')
  },
)

/**
 * Returns true if the current user's EAP session count is exhausted.
 * See getIsChildEAPSessionCountExhausted for when searching for care for a child.
 */
export const getIsUserEAPSessionCountExhausted = createSelector(
  [
    (state, withoutOverrides: boolean = false) => getUserEAPSessionLimit(state, withoutOverrides),
    (state) => getUsedSessionCount(state),
    (state) => getNumberOfSessionsByProgram(state),
  ],
  (userEAPSessionLimit, usedSessionCount, numberOfSessionsByProgram) => {
    if (numberOfSessionsByProgram && isUnlimitedSessionLimit(numberOfSessionsByProgram.coaching)) {
      return false
    }
    return userEAPSessionLimit - (usedSessionCount ?? 0) <= 0
  },
)

export const getIsEAPSessionCountExhaustedByProgramGetter = createSelector(
  [
    (state) => getUsedSessionCountByProgram(state),
    (state) => getNumberOfSessionsByProgram(state),
    (state) => getNumberOfSessions(state),
    (state) => getUsedSessionCount(state),
    getIsUserEAPSessionCountExhausted,
  ],
  (
    usedSessionCountByProgram,
    numberOfSessionsByProgram,
    numberOfSessions,
    usedSessionCount,
    isEAPSessionCountExhausted,
  ) =>
    (programCategory: ProgramCategory | undefined) => {
      if (!numberOfSessionsByProgram || !usedSessionCountByProgram || !programCategory) {
        return isEAPSessionCountExhausted
      }
      const programSessions = numberOfSessionsByProgram[programCategory]
      if (programSessions !== undefined) {
        const usedSessions = usedSessionCountByProgram[programCategory] ?? 0
        return programSessions - usedSessions <= 0
      }
      // Category is not explicitly configured (e.g. only {neurodiversity: 4} is listed) — it draws
      // from the shared remainder bucket. Previously this returned false unconditionally, leaving
      // therapy/coaching bookable even after the remainder was used up.
      const totalSessions = numberOfSessions ? parseInt(numberOfSessions, 10) : NaN
      if (Number.isNaN(totalSessions)) {
        return isEAPSessionCountExhausted
      }
      const remainderLimit = computeRemainderSessions(totalSessions, numberOfSessionsByProgram)
      const remainderUsed = computeRemainderUsed(
        usedSessionCount ?? 0,
        numberOfSessionsByProgram,
        usedSessionCountByProgram,
      )
      return remainderLimit - remainderUsed <= 0
    },
)

export const getIsUserGoogleCriticalTalent = createSelector(getUser, (user) => {
  return user?.get('isUserGoogleCriticalTalent') ?? false
})

// Flag is on, user is domestic, is not a Lyra select customer, and client is not disabled
export const getIsFavoritedProvidersFeatureAvailable = createSelector(
  [
    (state) => getIsFavoritedProvidersEnabled(state),
    getIsUserInternational,
    (state) => getCustomerProductType(state),
    (state) => getIsClientDisabled(state),
  ],
  (isFavoritedProvidersEnabled, isUserInternational, customerProductType, isClientDisabled) => {
    return (
      isFavoritedProvidersEnabled &&
      !isUserInternational &&
      customerProductType === DEFAULT_PRODUCT_TYPE &&
      !isClientDisabled
    )
  },
)

export const getCancellationLimitCoversSessionLimit = createSelector(
  [(state) => getCancellationLimit(state), getUserEAPSessionLimit],
  (cancellationLimit, userEAPSessionLimit) => {
    return cancellationLimit === userEAPSessionLimit.toString()
  },
)

const hasAcceptedBooleanUserProperty = (value: unknown): boolean => value === true || value === 'true'

export const getUserHasConsentedToAiChat = createSelector(getUser, (user) => {
  return hasAcceptedBooleanUserProperty(user?.get('hasConsentedToAIChat'))
})

export const getUserHasConsentedToMgcAiChat = createSelector(getUser, (user) => {
  return hasAcceptedBooleanUserProperty(user?.get('hasConsentedToMGCAIChat'))
})

export const getUserSeatBasedLicenses = createSelector(getUser, (user): SeatBasedLicensesResponse | undefined =>
  user?.get('seatBasedLicenses')?.toJS(),
)

export const getUserSeatBasedLicensesError = createSelector(
  getUser,
  (user): boolean => user?.get('seatBasedLicensesError') ?? false,
)

export const getManagerCoachingSeatEligibility = createSelector(
  getUserSeatBasedLicenses,
  getUserSeatBasedLicensesError,
  (data, isError): ManagerCoachingSeatEligibility =>
    getManagerCoachingSeatEligibilityShared({ isLoading: false, isError, data }),
)

export const getIsManager = createSelector(getUser, (user): boolean | undefined => {
  const isManager = user?.get('isManager')
  return isManager == null ? undefined : String(isManager) === 'true'
})

export const getManagerHubPromotionSignal = createSelector(getUser, (user): number | undefined => {
  const managerHubPromotionSignal = user?.getIn(['promotionSignals', 'managerHub'])
  return typeof managerHubPromotionSignal === 'number' ? managerHubPromotionSignal : undefined
})
